// @skade/rbac — rollebasert tilgangsstyring for SKADE.
//
// Modellen speiler Heimevernets kommandokjede. Tilgang UTLEDES av rolle +
// organisatorisk plassering (unngår manuell TAK-gruppekonfig, som er den
// vanligste feilkilden i TAK-felt). Ren, avhengighetsfri policy-motor slik at
// den kan enhetstestes og kjøres både i web-klient (UI-gating) og i backend
// (autoritativ håndhevelse — klient-sjekk er aldri nok alene).
import { echelonRank } from '@skade/contracts';
/** Nasjonalt/fellesoperativt nivå (FOH) — IKKE et HV-distrikt. Brukes som
 * org.district-markør for FOH, som per definisjon ser på tvers av ALLE
 * distrikter (og forsvarsgrener). Samme prinsipp ville gjelde et
 * fellesoperativt luft-/sjø-hovedkvarter: over distriktsstrukturen, ikke i den. */
export const NATIONAL_DISTRICT = 'NASJONAL';
/** Er org-plasseringen på nasjonalt nivå (FOH/fellesoperativ), ikke et distrikt? */
export function isNational(org) {
    return org.district === NATIONAL_DISTRICT;
}
const ROLE_RANK = {
    menig: 0,
    lagfører: 1,
    troppssjef: 2,
    områdesjef: 3,
    distriktsstab: 4,
    foh: 5,
};
/**
 * Grunnkapabiliteter per rolle: minste rolle-nivå som gir (resource, action).
 * Alt som ikke står her er nektet (default deny). Organisatorisk rekkevidde
 * håndheves separat av `inScope`.
 */
const CAPABILITIES = [
    { resource: 'room', action: 'read', minRole: 'menig' },
    { resource: 'room', action: 'write', minRole: 'menig' },
    { resource: 'track', action: 'read', minRole: 'menig' },
    { resource: 'track', action: 'write', minRole: 'menig' },
    { resource: 'sensor', action: 'read', minRole: 'menig' },
    { resource: 'spark_control', action: 'read', minRole: 'menig' },
    // Autorisering/lagsetting av SPARK: lagfører og opp (= «den som autoriserer»).
    { resource: 'spark_control', action: 'write', minRole: 'lagfører' },
    { resource: 'spark_control', action: 'authorize', minRole: 'lagfører' },
    { resource: 'geofence', action: 'read', minRole: 'menig' },
    { resource: 'geofence', action: 'write', minRole: 'troppssjef' },
    // ESO-sjekklister: alle ser/kvitterer; lagfører og opp DISTRIBUERER (command)
    // nedover kommandokjeden. Echelon-grensen håndheves av canDistributeChecklist.
    { resource: 'checklist', action: 'read', minRole: 'menig' },
    { resource: 'checklist', action: 'write', minRole: 'menig' },
    { resource: 'checklist', action: 'command', minRole: 'lagfører' },
    { resource: 'target_list', action: 'read', minRole: 'troppssjef' },
    { resource: 'target_list', action: 'write', minRole: 'områdesjef' },
    { resource: 'target_list', action: 'command', minRole: 'områdesjef' },
    { resource: 'video', action: 'read', minRole: 'områdesjef' },
    { resource: 'export', action: 'export', minRole: 'distriktsstab' },
    { resource: 'federation', action: 'write', minRole: 'distriktsstab' },
    { resource: 'audit', action: 'read', minRole: 'distriktsstab' },
    { resource: 'audit', action: 'admin', minRole: 'foh' },
    // GEOINT: alle i scope kan LESE kø/produkter (de påvirkes av hvilken node
    // som er okkupert); å LEGGE INN eller AVBRYTE en jobb krever lagfører og opp
    // — én ODM-jobb okkuperer hele noden i en time (~1 GB RAM/tråd), og det skal
    // ikke en enkelt menig kunne gjøre uten at noen med kommandoansvar står bak.
    { resource: 'geoint', action: 'read', minRole: 'menig' },
    { resource: 'geoint', action: 'write', minRole: 'lagfører' },
    { resource: 'geoint', action: 'command', minRole: 'lagfører' },
    // Personregisteret (ORBAT-person-binding): å SE mannskapslista (roster) for
    // egen enhet er noe en lagfører har bruk for uansett (samme gulv som
    // verifisering — `MIN_VERIFISERER_ROLLE` i verifisering.ts). Å SKRIVE inn
    // eller flytte en binding, eller overstyre navn/grad/rolle, er en ADMIN-
    // OVERSTYRING som omgår kommandolinje-sjekken helt (`kanVerifisere` gjelder
    // bare selve VERIFISERINGEN) — derfor et høyere gulv, distriktsstab+, samme
    // linje som andre brede overstyringer her (federation/export/audit-admin).
    // Selve verifiseringen/tilbakekallet bruker IKKE denne tabellen: den
    // myndigheten er kommandolinje-basert (`kanVerifisere`), ikke rollebasert.
    { resource: 'personnel', action: 'read', minRole: 'lagfører' },
    { resource: 'personnel', action: 'write', minRole: 'distriktsstab' },
    // Kapabilitetsregister + bestilling (vale/RFI_SENSORPLASSERING_LLM_PLAN_2026-08-19.md
    // §3/§8, eiermål 2026-08-19). Samme todeling som `checklist`: å LESE hva som
    // er utplassert, eller å BE OM (bestille) en kapabilitet — inkl. en menig som
    // melder seg selv tilgjengelig som `soldat-mobil` — er noe alle skal kunne
    // gjøre. Å ADMINISTRERE registeret (plassere/redigere/slette en kapabilitet,
    // godkjenne/avvise en bestilling) er en føringsbeslutning, samme gulv som
    // `spark_control`/`geofence`-skriving.
    { resource: 'kapabilitet', action: 'read', minRole: 'menig' },
    { resource: 'kapabilitet', action: 'command', minRole: 'menig' },
    { resource: 'kapabilitet', action: 'write', minRole: 'lagfører' },
    // Varsler-feeden (FEED-03, samme todeling som `kapabilitet`/`checklist`): å
    // LESE feeden, eller å SENDE et innlegg (en menig som melder en observasjon
    // eller en stab som legger ut en kunngjøring), er noe alle skal kunne gjøre —
    // `command` er lavere gulv enn å ADMINISTRERE feed-innstillinger (f.eks.
    // trekke tilbake/redigere ANDRES innlegg), som er en føringsbeslutning på
    // samme nivå som `spark_control`/`geofence`-skriving.
    { resource: 'feed', action: 'read', minRole: 'menig' },
    { resource: 'feed', action: 'command', minRole: 'menig' },
    { resource: 'feed', action: 'write', minRole: 'lagfører' },
];
/** Er brukerens org-plassering innenfor rekkevidden av ressursens org? */
export function inScope(user, ref) {
    // FOH ser alt, på tvers av distrikt.
    if (user.role === 'foh')
        return true;
    const o = ref.org;
    if (!o)
        return true; // ressurs uten org-binding (global lesing tillates av rolle)
    // Må minst dele distrikt.
    if (o.district !== user.org.district)
        return false;
    // Distriktsstab ser hele distriktet.
    if (user.role === 'distriktsstab')
        return true;
    // Områdesjef ser eget område.
    if (user.role === 'områdesjef')
        return !o.area || o.area === user.org.area;
    // Troppssjef ser egen tropp (og eget område implisitt).
    if (user.role === 'troppssjef') {
        return (!o.area || o.area === user.org.area) && (!o.troop || o.troop === user.org.troop);
    }
    // Lagfører/menig ser eget lag.
    return ((!o.area || o.area === user.org.area) &&
        (!o.troop || o.troop === user.org.troop) &&
        (!o.team || o.team === user.org.team));
}
/** Ressurstyper der «eierskap» gir mening (egen posisjon/melding/rom). */
const OWNABLE = new Set(['track', 'room']);
/** Sentral tilgangsavgjørelse. Default deny. */
export function can(user, action, ref) {
    // Eksplisitt grant (unntak) — fortsatt begrenset av org-scope.
    const granted = user.grants?.some((g) => g.resource === ref.resource && g.action === action);
    // «Egne» ressurser: les/skriv egen posisjon/melding — KUN for eierbare typer,
    // slik at en påsatt ownerId ikke omgår rolle/scope på target_list/audit osv.
    if (ref.ownerId &&
        ref.ownerId === user.id &&
        OWNABLE.has(ref.resource) &&
        (action === 'read' || action === 'write')) {
        return true;
    }
    const cap = CAPABILITIES.find((c) => c.resource === ref.resource && c.action === action);
    const roleOk = cap ? ROLE_RANK[user.role] >= ROLE_RANK[cap.minRole] : false;
    if (!roleOk && !granted)
        return false;
    // Fail-closed scope: for alt annet enn lesing MÅ ressursen ha en org-binding,
    // ellers deaktiveres org-avgrensningen stille (en glemt org skal aldri gi en
    // lagfører rett til å autorisere/kommandere på tvers av hele forsvaret).
    if (action !== 'read' && !ref.org && user.role !== 'foh')
        return false;
    return inScope(user, ref);
}
/** Praktisk hjelper for UI: hvilke handlinger kan brukeren gjøre på ressursen. */
export function allowedActions(user, ref) {
    const all = ['read', 'write', 'command', 'authorize', 'export', 'admin'];
    return all.filter((a) => can(user, a, ref));
}
// ── Echelon-bevisst sjekkliste-distribusjon (hierarki-akse 1) ────────────────
// Echelon-ordenen kommer fra ÉN kilde (@skade/contracts/hierarchy) — RBAC eier
// bare koblingen rolle → høyeste echelon rollen kan distribuere sjekklister til.
/** Høyeste kommando-echelon en rolle kan DISTRIBUERE sjekklister til/på. */
export const ROLE_COMMAND_ECHELON = {
    menig: 'enkeltmann',
    lagfører: 'lag',
    troppssjef: 'tropp',
    områdesjef: 'kompani',
    distriktsstab: 'brigade',
    foh: 'brigade',
};
/**
 * Kan brukeren distribuere en sjekkliste til `target` (echelon + territorium)?
 * Krever: (1) command-rett på 'checklist', (2) målets echelon er PÅ eller UNDER
 * rollens kommando-echelon (en lagfører kan ikke tildele en bataljon), og
 * (3) målet ligger innenfor brukerens org-scope. Default deny.
 */
/**
 * Brukerens MYNDIGHETSNIVÅ (til myndighetsmatrisen i contracts). Skiller seg
 * fra ROLE_COMMAND_ECHELON på ett punkt: FOH er 'nasjonal', ikke 'brigade' —
 * kommando-echelonet sier hvor stor styrke rollen fører, myndighetsnivået sier
 * hvor i beslutningsstigen den står, og for FOH er de to ikke det samme.
 */
export function myndighetsNivaa(user) {
    return user.role === 'foh' ? 'nasjonal' : ROLE_COMMAND_ECHELON[user.role];
}
/**
 * Kan brukeren utstede/trekke en myndighetsdelegasjon til `target`?
 * SCOPE-halvdelen av gaten: målet må ligge innenfor brukerens org-rekkevidde,
 * og echelon-taket gjelder som for sjekkliste-distribusjon. NIVÅ-halvdelen
 * (har utstederen myndigheten iboende, går den nedover, over gulvet) ligger i
 * contracts `kanDelegere` — begge må si ja. Default deny.
 */
export function canDelegateAuthority(user, target) {
    if (!can(user, 'command', { resource: 'checklist', org: targetOrg(target) }))
        return false;
    return true;
}
export function canDistributeChecklist(user, target) {
    if (!can(user, 'command', { resource: 'checklist', org: targetOrg(target) }))
        return false;
    const ceiling = ROLE_COMMAND_ECHELON[user.role];
    if (echelonRank(target.echelon) > echelonRank(ceiling))
        return false;
    return true;
}
function targetOrg(t) {
    return t.district ? { district: t.district, area: t.area, troop: t.troop, team: t.team } : undefined;
}
export { ROLE_RANK };
export * from './enrolment.js';
export * from './passord-reset.js';
export * from './abac.js';
export * from './verifisering.js';
//# sourceMappingURL=index.js.map